import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemText,
  MenuItem,
  TextField,
  Typography
} from "@material-ui/core";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const buildUserLabel = user =>
  [user?.name, user?.email].filter(Boolean).join(" - ");

const InternalChatConversationDialog = ({
  open,
  onClose,
  users,
  onCreated,
  canManageGroups = false
}) => {
  const [mode, setMode] = useState("direct");
  const [directUserId, setDirectUserId] = useState("");
  const [groupName, setGroupName] = useState("");
  const [groupUserIds, setGroupUserIds] = useState([]);
  const [saving, setSaving] = useState(false);

  const sortedUsers = useMemo(
    () =>
      Array.isArray(users)
        ? [...users].sort((left, right) =>
            String(left?.name || "").localeCompare(String(right?.name || ""))
          )
        : [],
    [users]
  );

  useEffect(() => {
    if (!open) {
      setMode("direct");
      setDirectUserId("");
      setGroupName("");
      setGroupUserIds([]);
    }
  }, [open]);

  useEffect(() => {
    if (!canManageGroups && mode === "group") {
      setMode("direct");
    }
  }, [canManageGroups, mode]);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const { data } =
        mode === "direct"
          ? await api.post("/internal-chats/direct", {
              userId: Number(directUserId)
            })
          : await api.post("/internal-chats/group", {
              name: groupName,
              userIds: groupUserIds.map(Number)
            });

      onCreated(data);
      onClose();
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const isDisabled =
    saving ||
    (mode === "direct" && !directUserId) ||
    (mode === "group" &&
      (!String(groupName || "").trim() || groupUserIds.length === 0));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="internal-chat-dialog-title"
    >
      <DialogTitle id="internal-chat-dialog-title">
        Nova conversa interna
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          select
          fullWidth
          margin="dense"
          variant="outlined"
          label="Tipo"
          value={mode}
          onChange={event => setMode(event.target.value)}
        >
          <MenuItem value="direct">Individual</MenuItem>
          {canManageGroups ? <MenuItem value="group">Grupo</MenuItem> : null}
        </TextField>

        {mode === "direct" ? (
          <TextField
            select
            fullWidth
            margin="dense"
            variant="outlined"
            label="Escolha o usuario"
            value={directUserId}
            onChange={event => setDirectUserId(Number(event.target.value))}
          >
            {sortedUsers.map(user => (
              <MenuItem key={user.id} value={user.id}>
                {buildUserLabel(user)}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <>
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              label="Nome do grupo"
              value={groupName}
              onChange={event => setGroupName(event.target.value)}
            />
            <TextField
              select
              fullWidth
              margin="dense"
              variant="outlined"
              label="Participantes"
              value={groupUserIds}
              onChange={event =>
                setGroupUserIds(
                  Array.isArray(event.target.value)
                    ? event.target.value.map(Number)
                    : []
                )
              }
              SelectProps={{
                multiple: true,
                renderValue: selected =>
                  sortedUsers
                    .filter(user => selected.includes(user.id))
                    .map(user => user.name)
                    .join(", ")
              }}
            >
              {sortedUsers.map(user => (
                <MenuItem key={user.id} value={user.id}>
                  <Checkbox checked={groupUserIds.includes(user.id)} />
                  <ListItemText primary={buildUserLabel(user)} />
                </MenuItem>
              ))}
            </TextField>
            <Typography variant="body2" color="textSecondary">
              O grupo sempre inclui voce automaticamente.
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          disabled={isDisabled}
        >
          Criar conversa
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InternalChatConversationDialog;
